import type { LeaveModel } from "./LeaveModel";
import type {
  LeaveRequest,
  LeaveType,
  CreateLeaveRequest,
  UpdateLeaveRequest,
  LeaveRequestsQuery,
  UserLeaveTypeBalance,
} from "./Leave.typed";
import type { PaginatedResponse } from "../zekay/pagination";
import type { MultiComment } from "../zekay/comments";

export class LeaveAdapter {
  constructor(private model: LeaveModel) {}

  async createLeaveRequest(request: CreateLeaveRequest): Promise<LeaveRequest> {
    return this.model.createLeaveRequest(request);
  }

  async getLeaveRequests(query?: LeaveRequestsQuery): Promise<PaginatedResponse<LeaveRequest>> {
    return this.model.getLeaveRequests(query);
  }

  async getLeaveRequest(id: number): Promise<LeaveRequest> {
    return this.model.getLeaveRequest(id);
  }

  async updateLeaveRequest(id: number, update: UpdateLeaveRequest): Promise<LeaveRequest> {
    return this.model.updateLeaveRequest(id, update);
  }

  async deleteLeaveRequest(id: number): Promise<void> {
    return this.model.deleteLeaveRequest(id);
  }

  async listLeaveTypes(): Promise<LeaveType[]> {
    return this.model.listLeaveTypes();
  }

  async getLeaveType(id: number): Promise<LeaveType> {
    return this.model.getLeaveType(id);
  }

  async createLeaveType(payload: Omit<LeaveType, "ID">): Promise<{ ID: number }> {
    return this.model.createLeaveType(payload);
  }

  async updateLeaveType(id: number, payload: Omit<LeaveType, "ID">): Promise<void> {
    return this.model.updateLeaveType(id, payload);
  }

  async deleteLeaveType(id: number): Promise<void> {
    return this.model.deleteLeaveType(id);
  }

  async listLeaveTypeBalances(
    typeId: number,
    page?: number,
    limit?: number,
  ): Promise<PaginatedResponse<UserLeaveTypeBalance>> {
    return this.model.listLeaveTypeBalances(typeId, page, limit);
  }

  async listBalancesByUser(
    userId: number,
    page?: number,
    limit?: number,
  ): Promise<PaginatedResponse<UserLeaveTypeBalance>> {
    return this.model.listBalancesByUser(userId, page, limit);
  }

  async setLeaveTypeBalance(
    typeId: number,
    userId: number,
    payload: { Balance?: number; MultiComments?: MultiComment[] },
  ): Promise<void> {
    return this.model.setLeaveTypeBalance(typeId, userId, payload);
  }

  async deleteLeaveTypeBalance(typeId: number, userId: number): Promise<void> {
    return this.model.deleteLeaveTypeBalance(typeId, userId);
  }

  async incrementLeaveTypeBalance(
    typeId: number,
    userId: number,
    payload: { Delta: number },
  ): Promise<void> {
    return this.model.incrementLeaveTypeBalance(typeId, userId, payload);
  }
}
